import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  ChatCircleText,
  CircleNotch,
  ArrowRight,
  UserCircle,
  Clock,
  Check,
  ArrowCounterClockwise,
  Phone,
  PhoneSlash,
  PhoneX,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { formatISTDateTime } from "@/lib/time";

function initials(name) {
  const src = (name || "?").trim();
  return src.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

export default function ChatActivityDialog({ open, onOpenChange, contact }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !contact?.phone) return;
    setLoading(true);
    setErr(null);
    api
      .get(`/conversations/${contact.phone}/activity`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setErr(e?.response?.data?.detail || e.message || "Load failed");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [open, contact?.phone]);

  const replies = data?.replies_by_agent || [];
  const history = data?.assignment_history || [];
  const ticketEvents = data?.ticket_events || [];
  const callHistory = data?.call_history || [];
  const totalReplies = replies.reduce((s, r) => s + (r.messages_sent || 0), 0);

  // Format a Tata call duration (seconds → "2m 15s" / "45s" / "—")
  const fmtDuration = (sec) => {
    const n = Math.max(0, Number(sec) || 0);
    if (n === 0) return "—";
    if (n < 60) return `${n}s`;
    const m = Math.floor(n / 60);
    const s = n % 60;
    return s ? `${m}m ${s}s` : `${m}m`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[88vh] overflow-y-auto"
        data-testid="chat-activity-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 heading">
            <UserCircle size={22} weight="duotone" className="text-[#60A5FA]" />
            Chat activity
          </DialogTitle>
          <DialogDescription>
            {contact?.name ? `${contact.name} · ` : ""}
            {contact?.phone ? `+${contact.phone}` : ""}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-[var(--muted)] text-sm gap-2">
            <CircleNotch size={18} className="animate-spin" /> Loading…
          </div>
        ) : err ? (
          <div className="py-10 text-center text-sm text-[#EF4444]">{err}</div>
        ) : (
          <div className="space-y-5">
            {/* Replies by agent */}
            <section data-testid="activity-replies-section">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
                  <ChatCircleText size={14} weight="duotone" className="text-[#60A5FA]" />
                  Replies by agent
                </div>
                <div className="text-[11px] text-[var(--muted)]">
                  {totalReplies} total
                </div>
              </div>
              {replies.length === 0 ? (
                <div className="text-xs text-[var(--muted)] py-3 text-center border border-dashed border-[var(--border)] rounded-md">
                  No agent has replied in this chat yet.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {replies.map((r) => (
                    <div
                      key={r.agent_id}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-md bg-[var(--bg)] border border-[var(--border)]"
                      data-testid={`activity-reply-${r.agent_id}`}
                    >
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarFallback className="bg-[var(--fg)] text-[var(--bg)] text-[10px]">
                          {initials(r.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium truncate leading-tight">
                          {r.full_name}
                        </div>
                        <div className="text-[10.5px] text-[var(--muted)] truncate leading-tight">
                          last reply: {formatISTDateTime(r.last_at)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-semibold text-sm heading leading-tight">
                          {r.messages_sent}
                        </div>
                        <div className="text-[10px] text-[var(--muted)] leading-tight">
                          msg{r.messages_sent === 1 ? "" : "s"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Assignment history */}
            <section data-testid="activity-assignments-section">
              <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
                <Clock size={14} weight="duotone" className="text-[#A78BFA]" />
                Assignment history
              </div>
              {history.length === 0 ? (
                <div className="text-xs text-[var(--muted)] py-3 text-center border border-dashed border-[var(--border)] rounded-md">
                  No assignment changes recorded for this chat.
                </div>
              ) : (
                <ol className="space-y-1.5" data-testid="activity-assignments-list">
                  {history.map((ev, i) => (
                    <li
                      key={i}
                      className="px-3 py-2 rounded-md bg-[var(--bg)] border border-[var(--border)] text-xs"
                      data-testid={`activity-assign-${i}`}
                    >
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[var(--muted)]">
                          {ev.from_name || (
                            <em className="opacity-60">unassigned</em>
                          )}
                        </span>
                        <ArrowRight size={11} className="text-[var(--muted)]" />
                        <span className="font-medium">
                          {ev.to_name || (
                            <em className="opacity-60">unassigned</em>
                          )}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[10.5px] text-[var(--muted)]">
                        by {ev.by_name || "—"} · {formatISTDateTime(ev.timestamp)}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            {/* Close / Re-open events — per user rule 2026-02-11 */}
            <section data-testid="activity-ticket-events-section">
              <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
                <Check size={14} weight="duotone" className="text-[#10B981]" />
                Close / Re-open history
              </div>
              {ticketEvents.length === 0 ? (
                <div className="text-xs text-[var(--muted)] py-3 text-center border border-dashed border-[var(--border)] rounded-md">
                  Chat has not been closed yet.
                </div>
              ) : (
                <ol className="space-y-1.5" data-testid="activity-ticket-events-list">
                  {ticketEvents.map((ev, i) => {
                    const isClose = ev.event === "close";
                    const isReopen = ev.event === "reopen";
                    const isCallResolved = ev.event === "call_request_resolved";
                    return (
                      <li
                        key={i}
                        className="px-3 py-2 rounded-md bg-[var(--bg)] border border-[var(--border)] text-xs"
                        data-testid={`activity-ticket-${i}`}
                      >
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {isClose && (
                            <Check size={12} weight="bold" className="text-[#10B981]" />
                          )}
                          {isReopen && (
                            <ArrowCounterClockwise size={12} weight="bold" className="text-[#F59E0B]" />
                          )}
                          {isCallResolved && (
                            <Check size={12} weight="bold" className="text-[#DC2626]" />
                          )}
                          <span
                            className={`font-semibold ${
                              isClose
                                ? "text-[#10B981]"
                                : isCallResolved
                                ? "text-[#DC2626]"
                                : "text-[#F59E0B]"
                            }`}
                          >
                            {isClose
                              ? "Closed"
                              : isCallResolved
                              ? "Call request resolved (silent)"
                              : "Re-opened"}
                          </span>
                          <span className="text-[var(--muted)]">by</span>
                          <span className="font-medium">{ev.agent_name || "—"}</span>
                          {(isClose || isCallResolved) && ev.silent && (
                            <span className="text-[10px] uppercase tracking-wide bg-[var(--bg-2)] text-[var(--muted)] px-1.5 py-0.5 rounded">
                              silent
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-[10.5px] text-[var(--muted)]">
                          {formatISTDateTime(ev.timestamp)}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            {/* Call history — per user rule 2026-02-12 ("agent jab call
                click karta hai uska bhi record chahiye, kis customer
                se, kab, kitni der baat ki"). Sourced from
                `tata_outbound_calls` (every click-to-call attempt) and
                matched with the Tata `tata_call_events` webhook within
                ±5 min to recover actual answered/missed status + the
                call duration in seconds. */}
            <section data-testid="activity-call-history-section">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
                  <Phone size={14} weight="duotone" className="text-[#10B981]" />
                  Call history
                </div>
                <div className="text-[11px] text-[var(--muted)]">
                  {callHistory.length} call{callHistory.length === 1 ? "" : "s"}
                </div>
              </div>
              {callHistory.length === 0 ? (
                <div className="text-xs text-[var(--muted)] py-3 text-center border border-dashed border-[var(--border)] rounded-md">
                  No outbound calls placed for this customer yet.
                </div>
              ) : (
                <ol className="space-y-1.5" data-testid="activity-call-history-list">
                  {callHistory
                    .slice()
                    .reverse() /* newest first */
                    .map((c, i) => {
                      const isAnswered = c.status === "answered";
                      const isMissed =
                        c.status === "missed" || c.status === "no_answer";
                      const isDialled = c.status === "dialled" && !isAnswered && !isMissed;
                      return (
                        <li
                          key={i}
                          className="px-3 py-2 rounded-md bg-[var(--bg)] border border-[var(--border)] text-xs"
                          data-testid={`activity-call-${i}`}
                        >
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {isAnswered && (
                              <Phone size={12} weight="fill" className="text-[#10B981]" />
                            )}
                            {isMissed && (
                              <PhoneX size={12} weight="fill" className="text-[#DC2626]" />
                            )}
                            {isDialled && (
                              <PhoneSlash size={12} weight="bold" className="text-[#F59E0B]" />
                            )}
                            <span
                              className={`font-semibold ${
                                isAnswered
                                  ? "text-[#10B981]"
                                  : isMissed
                                  ? "text-[#DC2626]"
                                  : "text-[#F59E0B]"
                              }`}
                            >
                              {isAnswered
                                ? "Answered"
                                : isMissed
                                ? "Not picked up"
                                : "Dialled"}
                            </span>
                            <span className="text-[var(--muted)]">by</span>
                            <span className="font-medium">{c.agent_name}</span>
                            {isAnswered && c.duration_seconds > 0 && (
                              <span
                                className="ml-auto text-[10.5px] font-mono font-semibold bg-[#10B98115] text-[#065F46] dark:text-[#A7F3D0] px-1.5 py-0.5 rounded"
                                data-testid={`activity-call-duration-${i}`}
                                title="Talk time"
                              >
                                {fmtDuration(c.duration_seconds)}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-[10.5px] text-[var(--muted)]">
                            {formatISTDateTime(c.timestamp)}
                            {!c.ok && (
                              <span className="ml-2 italic text-[#DC2626]">
                                (Tata API failed)
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                </ol>
              )}
            </section>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
